// lib/print-export.ts

import type { ImageExportFileFormat, Layer, PrintBleedMm, PrintExportOptions, PrintExportPreferences, PrintIccColorSpace, PrintIccProfile, PrintPresetId, PrintSnapshot, PrintSnapshotLayer, PrintSnapshotResource, Rect } from '#types/types';
import { getDrawingAwareSvgLayerSource } from '#lib/drawing-layer';
import { resolveStudioLayerTransform } from '#lib/studio-layer-transform';

export const PRINT_DPI_VALUES = [72, 100, 150, 300] as const;
export const PRINT_BLEED_VALUES = [0, 30, 50] as const;
export const PRINT_BAND_HEIGHT = 2048;
export const PRINT_TILE_SIZE = 2048;

export const planPrintTiles = (width: number): Array<{ x: number; width: number }> => {
  const tiles: Array<{ x: number; width: number }> = [];
  for (let x = 0; x < width; x += PRINT_TILE_SIZE) tiles.push({ x, width: Math.min(PRINT_TILE_SIZE, width - x) });
  return tiles;
};

export const planPrintBands = (height: number): Array<{ y: number; height: number }> => {
  const bands: Array<{ y: number; height: number }> = [];
  for (let y = 0; y < height; y += PRINT_BAND_HEIGHT) bands.push({ y, height: Math.min(PRINT_BAND_HEIGHT, height - y) });
  return bands;
};

export const mirrorBleedRgb = ({
  rgb,
  width,
  height,
  bandY,
  bandHeight,
  bleed,
  carryBefore,
}: {
  rgb: Uint8Array;
  width: number;
  height: number;
  bandY: number;
  bandHeight: number;
  bleed: number;
  carryBefore?: Uint8Array | null;
}): void => {
  if (bleed <= 0) return;
  // A missing carry row is an unavailable edge; white is the documented neutral fallback.
  const contentLeft = Math.min(width - 1, bleed);
  const contentRight = Math.max(contentLeft, width - bleed - 1);
  const contentTop = Math.min(height - 1, bleed);
  const contentBottom = Math.max(contentTop, height - bleed - 1);
  for (let localY = 0; localY < bandHeight; localY += 1) {
    const globalY = bandY + localY;
    const mirroredY = globalY < contentTop ? contentTop + (contentTop - globalY - 1) : (globalY > contentBottom ? contentBottom - (globalY - contentBottom - 1) : globalY);
    const sourceLocalY = mirroredY - bandY;
    for (let x = 0; x < width; x += 1) {
      const mirroredX = x < contentLeft ? contentLeft + (contentLeft - x - 1) : (x > contentRight ? contentRight - (x - contentRight - 1) : x);
      const destination = (localY * width + x) * 3;
      if (x < contentLeft || x > contentRight || globalY < contentTop || globalY > contentBottom) {
        const carryRows = carryBefore ? carryBefore.length / (width * 3) : 0;
        const sourceRow = sourceLocalY < 0 ? carryRows + sourceLocalY : sourceLocalY;
        const sourceBuffer = sourceLocalY < 0 ? carryBefore : rgb;
        if (!sourceBuffer || sourceRow < 0 || sourceRow >= sourceBuffer.length / (width * 3)) {
          rgb[destination] = 255; rgb[destination + 1] = 255; rgb[destination + 2] = 255;
          continue;
        }
        const source = (sourceRow * width + Math.max(0, Math.min(width - 1, mirroredX))) * 3;
        rgb[destination] = sourceBuffer[source] as number;
        rgb[destination + 1] = sourceBuffer[source + 1] as number;
        rgb[destination + 2] = sourceBuffer[source + 2] as number;
      }
    }
  }
};

export const mirrorBleedRgba = ({
  rgba,
  width,
  height,
  bandY,
  bandHeight,
  bleed,
}: {
  rgba: Uint8ClampedArray;
  width: number;
  height: number;
  bandY: number;
  bandHeight: number;
  bleed: number;
}): void => {
  if (bleed <= 0) return;
  const contentLeft = Math.min(width - 1, bleed);
  const contentRight = Math.max(contentLeft, width - bleed - 1);
  const contentTop = Math.min(height - 1, bleed);
  const contentBottom = Math.max(contentTop, height - bleed - 1);
  for (let localY = 0; localY < bandHeight; localY += 1) {
    const globalY = bandY + localY;
    const mirroredY = globalY < contentTop ? contentTop + (contentTop - globalY - 1) : (globalY > contentBottom ? contentBottom - (globalY - contentBottom - 1) : globalY);
    const sourceLocalY = mirroredY - bandY;
    for (let x = 0; x < width; x += 1) {
      const mirroredX = x < contentLeft ? contentLeft + (contentLeft - x - 1) : (x > contentRight ? contentRight - (x - contentRight - 1) : x);
      if (sourceLocalY < 0 || sourceLocalY >= bandHeight) continue;
      const source = (sourceLocalY * width + Math.max(0, Math.min(width - 1, mirroredX))) * 4;
      const destination = (localY * width + x) * 4;
      if (x < contentLeft || x > contentRight || globalY < contentTop || globalY > contentBottom) {
        rgba[destination] = rgba[source] as number;
        rgba[destination + 1] = rgba[source + 1] as number;
        rgba[destination + 2] = rgba[source + 2] as number;
        rgba[destination + 3] = rgba[source + 3] as number;
      }
    }
  }
};

export const PRINT_PRESETS: ReadonlyArray<{
  id: Exclude<PrintPresetId, 'custom'>;
  label: string;
  widthCm: number;
  heightCm: number;
  dpi: (typeof PRINT_DPI_VALUES)[number];
}> = [
  { id: 'standard-120x180', label: '1.20 × 1.80 m · 150 DPI', widthCm: 120, heightCm: 180, dpi: 150 },
  { id: 'standard-300x200', label: '3.00 × 2.00 m · 100 DPI', widthCm: 300, heightCm: 200, dpi: 100 },
  { id: 'standard-600x250', label: '6.00 × 2.50 m · 72 DPI', widthCm: 600, heightCm: 250, dpi: 72 },
];

export const PRINT_ICC_PROFILES: Readonly<Record<PrintIccProfile, {
  label: string;
  path: string;
  description: string;
  colorSpace: PrintIccColorSpace;
}>> = {
  'adobe-rgb-1998': {
    label: 'Adobe RGB (1998)',
    path: '/assets/icc/adobe-rgb-1998.icc',
    description: 'Adobe RGB (1998)',
    colorSpace: 'rgb',
  },
  srgb: {
    label: 'sRGB IEC61966-2.1',
    path: '/assets/icc/srgb-iec61966-2.1.icc',
    description: 'sRGB IEC61966-2.1',
    colorSpace: 'rgb',
  },
  'iso-coated-v2-300-eci': {
    label: 'CMYK — ISO Coated v2 300% (ECI)',
    path: '/assets/icc/iso-coated-v2-300-eci.icc',
    description: 'ISO Coated v2 300% (ECI)',
    colorSpace: 'cmyk',
  },
};

export const mmToPrintPixels = (millimetres: number, dpi: number): number => (
  Math.round((millimetres / 25.4) * dpi)
);

export const resolvePrintDimensions = ({
  widthCm,
  heightCm,
  dpi,
  bleedMm,
}: Pick<PrintExportOptions, 'widthCm' | 'heightCm' | 'dpi' | 'bleedMm'>): { width: number; height: number; widthMm: number; heightMm: number } => {
  const safeBleed = bleedMm === 30 || bleedMm === 50 ? bleedMm : 0;
  const widthMm = widthCm * 10 + safeBleed * 2;
  const heightMm = heightCm * 10 + safeBleed * 2;
  const bleedPx = mmToPrintPixels(safeBleed, dpi);
  return {
    width: mmToPrintPixels(widthCm * 10, dpi) + bleedPx * 2,
    height: mmToPrintPixels(heightCm * 10, dpi) + bleedPx * 2,
    widthMm,
    heightMm,
  };
};

export const resolvePrintPreset = (preset: PrintPresetId): Pick<PrintExportOptions, 'widthCm' | 'heightCm' | 'dpi'> | null => {
  if (preset === 'custom') return null;
  const match = PRINT_PRESETS.find((candidate) => candidate.id === preset);
  return match ? { widthCm: match.widthCm, heightCm: match.heightCm, dpi: match.dpi } : null;
};

export const syncPrintDimensions = ({
  widthCm,
  heightCm,
  changedDimension,
  valueCm,
}: {
  widthCm: number;
  heightCm: number;
  changedDimension: 'widthCm' | 'heightCm';
  valueCm: number;
}): Pick<PrintExportOptions, 'widthCm' | 'heightCm'> => {
  if (!Number.isFinite(valueCm) || valueCm <= 0 || !Number.isFinite(widthCm) || widthCm <= 0 || !Number.isFinite(heightCm) || heightCm <= 0) {
    return changedDimension === 'widthCm' ? { widthCm: valueCm, heightCm } : { widthCm, heightCm: valueCm };
  }
  const aspectRatio = widthCm / heightCm;
  return changedDimension === 'widthCm'
    ? { widthCm: valueCm, heightCm: valueCm / aspectRatio }
    : { widthCm: valueCm * aspectRatio, heightCm: valueCm };
};

export const validatePrintExportOptions = (options: PrintExportOptions): string | null => {
  if (!options || options.format !== 'tiff' && options.format !== 'jpeg') return 'PRINT_INVALID_OPTIONS';
  if (!Number.isFinite(options.widthCm) || options.widthCm <= 0) return 'PRINT_INVALID_DIMENSIONS';
  if (!Number.isFinite(options.heightCm) || options.heightCm <= 0) return 'PRINT_INVALID_DIMENSIONS';
  if (!PRINT_DPI_VALUES.includes(options.dpi)) return 'PRINT_INVALID_DPI';
  if (!PRINT_BLEED_VALUES.includes(options.bleedMm)) return 'PRINT_INVALID_BLEED';
  if (options.preset !== 'custom' && !resolvePrintPreset(options.preset)) return 'PRINT_INVALID_PRESET';
  if (!PRINT_ICC_PROFILES[options.iccProfile]) return 'PRINT_INVALID_ICC';
  if (options.format === 'jpeg' && PRINT_ICC_PROFILES[options.iccProfile].colorSpace === 'cmyk') return 'PRINT_CMYK_JPEG_UNSUPPORTED';
  const dimensions = resolvePrintDimensions(options);
  if (!Number.isSafeInteger(dimensions.width) || !Number.isSafeInteger(dimensions.height) || dimensions.width < 1 || dimensions.height < 1) {
    return 'PRINT_INVALID_DIMENSIONS';
  }
  if (options.format === 'jpeg' && (dimensions.width > 65535 || dimensions.height > 65535)) return 'PRINT_INVALID_DIMENSIONS';
  return null;
};

export const normalizePrintPreferences = (value: unknown): PrintExportPreferences | null => {
  if (!value || typeof value !== 'object') return null;
  const raw = value as Partial<PrintExportPreferences>;
  const preset = raw.preset === 'standard-120x180' || raw.preset === 'standard-300x200' || raw.preset === 'standard-600x250' || raw.preset === 'custom'
    ? raw.preset : null;
  const dpi = raw.dpi === 72 || raw.dpi === 100 || raw.dpi === 150 || raw.dpi === 300 ? raw.dpi : null;
  const format = raw.format === 'tiff' || raw.format === 'jpeg' ? raw.format : null;
  const iccProfile = raw.iccProfile === 'adobe-rgb-1998' || raw.iccProfile === 'srgb' || raw.iccProfile === 'iso-coated-v2-300-eci' ? raw.iccProfile : null;
  const bleedMm: PrintBleedMm | null = raw.bleedMm === 0 || raw.bleedMm === 30 || raw.bleedMm === 50 ? raw.bleedMm : null;
  if (!preset || !dpi || !format || !iccProfile || bleedMm === null) return null;
  if (!Number.isFinite(raw.widthCm) || !Number.isFinite(raw.heightCm) || Number(raw.widthCm) <= 0 || Number(raw.heightCm) <= 0) return null;
  return {
    preset,
    widthCm: Number(raw.widthCm),
    heightCm: Number(raw.heightCm),
    dpi,
    format: format === 'jpeg' && PRINT_ICC_PROFILES[iccProfile].colorSpace === 'cmyk' ? 'tiff' : format,
    iccProfile,
    bleedMm,
    target: raw.target === 'frame' ? 'frame' : 'area',
  };
};

export const defaultPrintOptions = (target: 'area' | 'frame' = 'area'): PrintExportOptions => ({
  preset: 'standard-120x180',
  widthCm: 120,
  heightCm: 180,
  dpi: 150,
  format: 'tiff',
  iccProfile: 'adobe-rgb-1998',
  bleedMm: 0,
  target,
});

const concatBytes = (chunks: Uint8Array[]): Uint8Array => {
  const total = chunks.reduce((sum, chunk) => sum + chunk.byteLength, 0);
  const result = new Uint8Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    result.set(chunk, offset);
    offset += chunk.byteLength;
  }
  return result;
};

const ascii = (value: string): Uint8Array => Uint8Array.from(value, (character) => character.charCodeAt(0));

const writeU16 = (view: DataView, offset: number, value: number) => view.setUint16(offset, value, true);
const writeU32 = (view: DataView, offset: number, value: number) => view.setUint32(offset, value, true);

export const lzwDecode = (input: Uint8Array): Uint8Array => {
  const output: number[] = [];
  const dictionary: number[][] = [];
  let codeSize = 9;
  let nextCode = 258;
  let bitBuffer = 0;
  let bitCount = 0;
  let offset = 0;
  const readCode = (): number | null => {
    while (bitCount < codeSize && offset < input.length) { bitBuffer = (bitBuffer << 8) | (input[offset++] as number); bitCount += 8; }
    if (bitCount < codeSize) return null;
    const code = (bitBuffer >>> (bitCount - codeSize)) & ((1 << codeSize) - 1); bitCount -= codeSize;
    bitBuffer &= bitCount > 0 ? (1 << bitCount) - 1 : 0;
    return code;
  };
  const reset = () => { dictionary.length = 256; for (let index = 0; index < 256; index += 1) dictionary[index] = [index]; nextCode = 258; codeSize = 9; };
  reset();
  let previous: number[] | null = null;
  while (true) {
    const code = readCode(); if (code === null || code === 257) break;
    if (code === 256) { reset(); previous = null; continue; }
    const phrase: number[] = dictionary[code] ?? (previous ? [...previous, previous[0] as number] : []);
    if (phrase.length === 0) throw new Error('Invalid TIFF LZW stream');
    output.push(...phrase);
    if (previous && nextCode < 4096) {
      dictionary[nextCode++] = [...previous, phrase[0] as number];
      if (nextCode === (1 << codeSize) - 1 && codeSize < 12) codeSize += 1;
    }
    previous = phrase;
  }
  return Uint8Array.from(output);
};

export const lzwEncode = (input: Uint8Array): Uint8Array => {
  const clear = 256;
  const end = 257;
  const dictionary = new Map<string, number>();
  const reset = () => {
    dictionary.clear();
    for (let i = 0; i < 256; i += 1) dictionary.set(String.fromCharCode(i), i);
  };
  reset();
  let nextCode = 258;
  let codeSize = 9;
  let bitBuffer = 0;
  let bitCount = 0;
  const output: number[] = [];
  const emit = (code: number) => {
    bitBuffer = (bitBuffer << codeSize) | code;
    bitCount += codeSize;
    while (bitCount >= 8) {
      bitCount -= 8;
      output.push((bitBuffer >>> bitCount) & 255);
      bitBuffer &= bitCount > 0 ? (1 << bitCount) - 1 : 0;
    }
  };
  emit(clear);
  if (input.length > 0) {
    let phrase = String.fromCharCode(input[0] as number);
    for (let i = 1; i < input.length; i += 1) {
      const next = String.fromCharCode(input[i] as number);
      const joined = phrase + next;
      const existing = dictionary.get(joined);
      if (existing !== undefined) {
        phrase = joined;
        continue;
      }
      emit(dictionary.get(phrase) as number);
      if (nextCode < 4096) {
        dictionary.set(joined, nextCode);
        nextCode += 1;
        if (nextCode === (1 << codeSize) && codeSize < 12) codeSize += 1;
      } else {
        emit(clear);
        reset();
        nextCode = 258;
        codeSize = 9;
      }
      phrase = next;
    }
    emit(dictionary.get(phrase) as number);
  }
  emit(end);
  if (bitCount > 0) output.push((bitBuffer << (8 - bitCount)) & 255);
  return Uint8Array.from(output);
};

const makeRational = (value: number): Uint8Array => {
  const bytes = new Uint8Array(8);
  const view = new DataView(bytes.buffer);
  writeU32(view, 0, Math.max(1, Math.round(value * 1000)));
  writeU32(view, 4, 1000);
  return bytes;
};

export const convertRgbPixelToCmyk = (r: number, g: number, b: number): [number, number, number, number] => {
  const cyan = 255 - Math.max(0, Math.min(255, Math.round(r)));
  const magenta = 255 - Math.max(0, Math.min(255, Math.round(g)));
  const yellow = 255 - Math.max(0, Math.min(255, Math.round(b)));
  const black = Math.min(cyan, magenta, yellow);
  if (black === 255) return [0, 0, 0, 255];
  const scale = 255 - black;
  return [
    Math.round(((cyan - black) * 255) / scale),
    Math.round(((magenta - black) * 255) / scale),
    Math.round(((yellow - black) * 255) / scale),
    black,
  ];
};

export const convertRgbToCmyk = (rgb: Uint8Array): Uint8Array => {
  if (rgb.byteLength % 3 !== 0) throw new Error('PRINT_INVALID_RGB_DATA');
  const cmyk = new Uint8Array((rgb.byteLength / 3) * 4);
  for (let source = 0, destination = 0; source < rgb.byteLength; source += 3, destination += 4) {
    cmyk.set(convertRgbPixelToCmyk(rgb[source] as number, rgb[source + 1] as number, rgb[source + 2] as number), destination);
  }
  return cmyk;
};

export const encodeTiffRgbLzw = ({
  width,
  height,
  dpi,
  icc,
  strips,
  rowsPerStrip,
  stripsAreCompressed,
}: {
  width: number;
  height: number;
  dpi: number;
  icc: Uint8Array;
  strips: Uint8Array[];
  rowsPerStrip: number;
  stripsAreCompressed?: boolean;
}): Uint8Array => {
  const compressedStrips = stripsAreCompressed ? strips : strips.map(lzwEncode);
  const entryCount = 14;
  const ifdOffset = 8;
  const ifdSize = 2 + entryCount * 12 + 4;
  const bitsOffset = ifdOffset + ifdSize;
  const stripOffsetsOffset = bitsOffset + 6;
  const stripByteCountsOffset = stripOffsetsOffset + compressedStrips.length * 4;
  const xResolutionOffset = stripByteCountsOffset + compressedStrips.length * 4;
  const yResolutionOffset = xResolutionOffset + 8;
  const iccOffset = yResolutionOffset + 8;
  let dataOffset = iccOffset + icc.byteLength;
  const stripOffsets = compressedStrips.map((strip) => {
    const offset = dataOffset;
    dataOffset += strip.byteLength;
    return offset;
  });
  const output = new Uint8Array(dataOffset);
  const view = new DataView(output.buffer);
  output.set([0x49, 0x49, 0x2a, 0x00], 0);
  writeU32(view, 4, ifdOffset);
  writeU16(view, ifdOffset, entryCount);
  const entries: Array<[number, number, number, number]> = [
    [256, 4, 1, width],
    [257, 4, 1, height],
    [258, 3, 3, bitsOffset],
    [259, 3, 1, 5],
    [262, 3, 1, 2],
    [273, 4, compressedStrips.length, compressedStrips.length === 1 ? (stripOffsets[0] as number) : stripOffsetsOffset],
    [277, 3, 1, 3],
    [278, 4, 1, rowsPerStrip],
    [279, 4, compressedStrips.length, compressedStrips.length === 1 ? (compressedStrips[0]?.byteLength ?? 0) : stripByteCountsOffset],
    [282, 5, 1, xResolutionOffset],
    [283, 5, 1, yResolutionOffset],
    [284, 3, 1, 1],
    [296, 3, 1, 2],
    [34675, 7, icc.byteLength, iccOffset],
  ];
  entries.forEach(([tag, type, count, value], index) => {
    const offset = ifdOffset + 2 + index * 12;
    writeU16(view, offset, tag);
    writeU16(view, offset + 2, type);
    writeU32(view, offset + 4, count);
    if (type === 3 && count === 1) writeU16(view, offset + 8, value);
    else writeU32(view, offset + 8, value);
  });
  writeU32(view, ifdOffset + 2 + entryCount * 12, 0);
  output.set([8, 0, 8, 0, 8, 0], bitsOffset);
  compressedStrips.forEach((strip, index) => {
    writeU32(view, stripOffsetsOffset + index * 4, stripOffsets[index] as number);
    writeU32(view, stripByteCountsOffset + index * 4, strip.byteLength);
  });
  output.set(makeRational(dpi), xResolutionOffset);
  output.set(makeRational(dpi), yResolutionOffset);
  output.set(icc, iccOffset);
  compressedStrips.forEach((strip, index) => output.set(strip, stripOffsets[index] as number));
  return output;
};

export const encodeTiffCmykLzw = ({
  width,
  height,
  dpi,
  icc,
  strips,
  rowsPerStrip,
  stripsAreCompressed,
}: {
  width: number;
  height: number;
  dpi: number;
  icc: Uint8Array;
  strips: Uint8Array[];
  rowsPerStrip: number;
  stripsAreCompressed?: boolean;
}): Uint8Array => {
  const compressedStrips = stripsAreCompressed ? strips : strips.map(lzwEncode);
  const bits = Uint8Array.from([8, 0, 8, 0, 8, 0, 8, 0]);
  const inkNames = ascii('Cyan\0Magenta\0Yellow\0Black\0');
  const entryCount = 17;
  const ifdOffset = 8;
  const ifdSize = 2 + entryCount * 12 + 4;
  const bitsOffset = ifdOffset + ifdSize;
  const stripOffsetsOffset = bitsOffset + bits.byteLength;
  const stripByteCountsOffset = stripOffsetsOffset + compressedStrips.length * 4;
  const xResolutionOffset = stripByteCountsOffset + compressedStrips.length * 4;
  const yResolutionOffset = xResolutionOffset + 8;
  const inkNamesOffset = yResolutionOffset + 8;
  const iccOffset = inkNamesOffset + inkNames.byteLength;
  let dataOffset = iccOffset + icc.byteLength;
  const stripOffsets = compressedStrips.map((strip) => {
    const offset = dataOffset;
    dataOffset += strip.byteLength;
    return offset;
  });
  const output = new Uint8Array(dataOffset);
  const view = new DataView(output.buffer);
  output.set([0x49, 0x49, 0x2a, 0x00], 0);
  writeU32(view, 4, ifdOffset);
  writeU16(view, ifdOffset, entryCount);
  const entries: Array<[number, number, number, number]> = [
    [256, 4, 1, width], [257, 4, 1, height], [258, 3, 4, bitsOffset], [259, 3, 1, 5], [262, 3, 1, 5],
    [273, 4, compressedStrips.length, compressedStrips.length === 1 ? (stripOffsets[0] as number) : stripOffsetsOffset],
    [277, 3, 1, 4], [278, 4, 1, rowsPerStrip],
    [279, 4, compressedStrips.length, compressedStrips.length === 1 ? (compressedStrips[0]?.byteLength ?? 0) : stripByteCountsOffset],
    [282, 5, 1, xResolutionOffset], [283, 5, 1, yResolutionOffset], [284, 3, 1, 1], [296, 3, 1, 2],
    [332, 3, 1, 1], [333, 2, inkNames.byteLength, inkNamesOffset], [334, 3, 1, 4], [34675, 7, icc.byteLength, iccOffset],
  ];
  entries.forEach(([tag, type, count, value], index) => {
    const offset = ifdOffset + 2 + index * 12;
    writeU16(view, offset, tag); writeU16(view, offset + 2, type); writeU32(view, offset + 4, count);
    if (type === 3 && count === 1) writeU16(view, offset + 8, value); else writeU32(view, offset + 8, value);
  });
  writeU32(view, ifdOffset + 2 + entryCount * 12, 0);
  output.set(bits, bitsOffset);
  compressedStrips.forEach((strip, index) => { writeU32(view, stripOffsetsOffset + index * 4, stripOffsets[index] as number); writeU32(view, stripByteCountsOffset + index * 4, strip.byteLength); });
  output.set(makeRational(dpi), xResolutionOffset); output.set(makeRational(dpi), yResolutionOffset);
  output.set(inkNames, inkNamesOffset); output.set(icc, iccOffset);
  compressedStrips.forEach((strip, index) => output.set(strip, stripOffsets[index] as number));
  return output;
};

const ZIGZAG = [0, 1, 5, 6, 14, 15, 27, 28, 2, 4, 7, 13, 16, 26, 29, 42, 3, 8, 12, 17, 25, 30, 41, 43, 9, 11, 18, 24, 31, 40, 44, 53, 10, 19, 23, 32, 39, 45, 52, 54, 20, 22, 33, 38, 46, 51, 55, 60, 21, 34, 37, 47, 50, 56, 59, 61, 35, 36, 48, 49, 57, 58, 62, 63];
const STD_LUMA_Q = [16, 11, 10, 16, 24, 40, 51, 61, 12, 12, 14, 19, 26, 58, 60, 55, 14, 13, 16, 24, 40, 57, 69, 56, 14, 17, 22, 29, 51, 87, 80, 62, 18, 22, 37, 56, 68, 109, 103, 77, 24, 35, 55, 64, 81, 104, 113, 92, 49, 64, 78, 87, 103, 121, 120, 101, 72, 92, 95, 98, 112, 100, 103, 99];
const STD_CHROMA_Q = [17, 18, 24, 47, 99, 99, 99, 99, 18, 21, 26, 66, 99, 99, 99, 99, 24, 26, 56, 99, 99, 99, 99, 99, 47, 66, 99, 99, 99, 99, 99, 99, 99, 99, 99, 99, 99, 99, 99, 99, 99, 99, 99, 99, 99, 99, 99, 99, 99, 99, 99, 99, 99, 99, 99, 99, 99, 99, 99, 99, 99, 99, 99, 99];
const DC_LUMA_BITS = [0, 0, 1, 5, 1, 1, 1, 1, 1, 1, 0, 0, 0, 0, 0, 0, 0];
const DC_LUMA_VALS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
const DC_CHROMA_BITS = [0, 0, 3, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0, 0, 0, 0, 0];
const DC_CHROMA_VALS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
const AC_LUMA_BITS = [0, 0, 2, 1, 3, 3, 2, 4, 3, 5, 5, 4, 4, 0, 0, 1, 0x7d];
const AC_LUMA_VALS = [0x01, 0x02, 0x03, 0x00, 0x04, 0x11, 0x05, 0x12, 0x21, 0x31, 0x41, 0x06, 0x13, 0x51, 0x61, 0x07, 0x22, 0x71, 0x14, 0x32, 0x81, 0x91, 0xa1, 0x08, 0x23, 0x42, 0xb1, 0xc1, 0x15, 0x52, 0xd1, 0xf0, 0x24, 0x33, 0x62, 0x72, 0x82, 0x09, 0x0a, 0x16, 0x17, 0x18, 0x19, 0x1a, 0x25, 0x26, 0x27, 0x28, 0x29, 0x2a, 0x34, 0x35, 0x36, 0x37, 0x38, 0x39, 0x3a, 0x43, 0x44, 0x45, 0x46, 0x47, 0x48, 0x49, 0x4a, 0x53, 0x54, 0x55, 0x56, 0x57, 0x58, 0x59, 0x5a, 0x63, 0x64, 0x65, 0x66, 0x67, 0x68, 0x69, 0x6a, 0x73, 0x74, 0x75, 0x76, 0x77, 0x78, 0x79, 0x7a, 0x83, 0x84, 0x85, 0x86, 0x87, 0x88, 0x89, 0x8a, 0x92, 0x93, 0x94, 0x95, 0x96, 0x97, 0x98, 0x99, 0x9a, 0xa2, 0xa3, 0xa4, 0xa5, 0xa6, 0xa7, 0xa8, 0xa9, 0xaa, 0xb2, 0xb3, 0xb4, 0xb5, 0xb6, 0xb7, 0xb8, 0xb9, 0xba, 0xc2, 0xc3, 0xc4, 0xc5, 0xc6, 0xc7, 0xc8, 0xc9, 0xca, 0xd2, 0xd3, 0xd4, 0xd5, 0xd6, 0xd7, 0xd8, 0xd9, 0xda, 0xe1, 0xe2, 0xe3, 0xe4, 0xe5, 0xe6, 0xe7, 0xe8, 0xe9, 0xea, 0xf1, 0xf2, 0xf3, 0xf4, 0xf5, 0xf6, 0xf7, 0xf8, 0xf9, 0xfa];
const AC_CHROMA_BITS = [0, 0, 2, 1, 2, 4, 4, 3, 4, 7, 5, 4, 4, 0, 1, 2, 0x77];
const AC_CHROMA_VALS = [0x00, 0x01, 0x02, 0x03, 0x11, 0x04, 0x05, 0x21, 0x31, 0x06, 0x12, 0x41, 0x51, 0x07, 0x61, 0x71, 0x13, 0x22, 0x32, 0x81, 0x08, 0x14, 0x42, 0x91, 0xa1, 0xb1, 0xc1, 0x09, 0x23, 0x33, 0x52, 0xf0, 0x15, 0x62, 0x72, 0xd1, 0x0a, 0x16, 0x24, 0x34, 0xe1, 0x25, 0xf1, 0x17, 0x18, 0x19, 0x1a, 0x26, 0x27, 0x28, 0x29, 0x2a, 0x35, 0x36, 0x37, 0x38, 0x39, 0x3a, 0x43, 0x44, 0x45, 0x46, 0x47, 0x48, 0x49, 0x4a, 0x53, 0x54, 0x55, 0x56, 0x57, 0x58, 0x59, 0x5a, 0x63, 0x64, 0x65, 0x66, 0x67, 0x68, 0x69, 0x6a, 0x73, 0x74, 0x75, 0x76, 0x77, 0x78, 0x79, 0x7a, 0x82, 0x83, 0x84, 0x85, 0x86, 0x87, 0x88, 0x89, 0x8a, 0x92, 0x93, 0x94, 0x95, 0x96, 0x97, 0x98, 0x99, 0x9a, 0xa2, 0xa3, 0xa4, 0xa5, 0xa6, 0xa7, 0xa8, 0xa9, 0xaa, 0xb2, 0xb3, 0xb4, 0xb5, 0xb6, 0xb7, 0xb8, 0xb9, 0xba, 0xc2, 0xc3, 0xc4, 0xc5, 0xc6, 0xc7, 0xc8, 0xc9, 0xca, 0xd2, 0xd3, 0xd4, 0xd5, 0xd6, 0xd7, 0xd8, 0xd9, 0xda, 0xe2, 0xe3, 0xe4, 0xe5, 0xe6, 0xe7, 0xe8, 0xe9, 0xea, 0xf2, 0xf3, 0xf4, 0xf5, 0xf6, 0xf7, 0xf8, 0xf9, 0xfa];

interface HuffmanCode { code: number; length: number; }
const buildHuffman = (bits: number[], values: number[]): HuffmanCode[] => {
  const table: HuffmanCode[] = [];
  let code = 0;
  let index = 0;
  for (let length = 1; length <= 16; length += 1) {
    for (let count = 0; count < (bits[length] ?? 0); count += 1) {
      table[values[index] as number] = { code, length };
      code += 1;
      index += 1;
    }
    code <<= 1;
  }
  return table;
};

const magnitude = (value: number): number => value === 0 ? 0 : Math.floor(Math.log2(Math.abs(value))) + 1;
const magnitudeBits = (value: number, size: number): number => value >= 0 ? value : value + (1 << size) - 1;

export class JpegRgbEncoder {
  readonly quality = 100;
  private readonly chunks: Uint8Array[] = [];
  private readonly entropyChunk = new Uint8Array(64 * 1024);
  private entropyOffset = 0;
  private bitBuffer = 0;
  private bitCount = 0;
  private dc = [0, 0, 0];
  private readonly dcTables = [buildHuffman(DC_LUMA_BITS, DC_LUMA_VALS), buildHuffman(DC_CHROMA_BITS, DC_CHROMA_VALS)];
  private readonly acTables = [buildHuffman(AC_LUMA_BITS, AC_LUMA_VALS), buildHuffman(AC_CHROMA_BITS, AC_CHROMA_VALS)];
  private readonly qTables = [this.scaleQuantization(STD_LUMA_Q), this.scaleQuantization(STD_CHROMA_Q)];

  constructor(private readonly width: number, private readonly height: number, private readonly dpi: number, private readonly icc: Uint8Array, quality: 100) {
    if (quality !== 100) throw new Error('PRINT_JPEG_QUALITY_MUST_BE_100');
    if (width > 65535 || height > 65535) throw new Error('PRINT_JPEG_DIMENSION_LIMIT');
    this.chunks.push(this.header());
  }

  private scaleQuantization(table: number[]): number[] {
    return table.map((value) => Math.max(1, Math.floor((value + 50) / 100)));
  }

  private marker(marker: number, payload: Uint8Array = new Uint8Array()): Uint8Array {
    const result = new Uint8Array(payload.length + 4);
    result[0] = 0xff; result[1] = marker;
    new DataView(result.buffer).setUint16(2, payload.length + 2, false);
    result.set(payload, 4);
    return result;
  }

  private header(): Uint8Array {
    const chunks: Uint8Array[] = [Uint8Array.from([0xff, 0xd8])];
    const jfif = new Uint8Array(14); jfif.set(ascii('JFIF\0')); jfif[5] = 1; jfif[6] = 2; jfif[7] = 1;
    new DataView(jfif.buffer).setUint16(8, this.dpi, false); new DataView(jfif.buffer).setUint16(10, this.dpi, false);
    chunks.push(this.marker(0xe0, jfif));
    const adobe = new Uint8Array(12); adobe.set(ascii('Adobe')); new DataView(adobe.buffer).setUint16(5, 100, false); adobe[11] = 0;
    chunks.push(this.marker(0xee, adobe));
    const maxChunk = 60000;
    for (let offset = 0; offset < this.icc.length; offset += maxChunk) {
      const part = this.icc.slice(offset, offset + maxChunk);
      const payload = new Uint8Array(14 + part.length); payload.set(ascii('ICC_PROFILE\0')); payload[12] = Math.floor(offset / maxChunk) + 1; payload[13] = Math.ceil(this.icc.length / maxChunk); payload.set(part, 14);
      chunks.push(this.marker(0xe2, payload));
    }
    const dqt = new Uint8Array(65 * 2); dqt[0] = 0; dqt.set(this.qTables[0]!.map((_, index) => this.qTables[0]![ZIGZAG[index] as number] as number), 1); dqt[65] = 1; dqt.set(this.qTables[1]!.map((_, index) => this.qTables[1]![ZIGZAG[index] as number] as number), 66); chunks.push(this.marker(0xdb, dqt));
    const sof = new Uint8Array([8, (this.height >> 8) & 255, this.height & 255, (this.width >> 8) & 255, this.width & 255, 3, 1, 0x11, 0, 2, 0x11, 1, 3, 0x11, 1]); chunks.push(this.marker(0xc0, sof));
    const dhtPayload: number[] = [];
    const addDht = (classId: number, id: number, bits: number[], values: number[]) => dhtPayload.push((classId << 4) | id, ...bits.slice(1), ...values);
    addDht(0, 0, DC_LUMA_BITS, DC_LUMA_VALS); addDht(1, 0, AC_LUMA_BITS, AC_LUMA_VALS); addDht(0, 1, DC_CHROMA_BITS, DC_CHROMA_VALS); addDht(1, 1, AC_CHROMA_BITS, AC_CHROMA_VALS);
    chunks.push(this.marker(0xc4, Uint8Array.from(dhtPayload)));
    chunks.push(this.marker(0xda, Uint8Array.from([3, 1, 0, 2, 0x11, 3, 0x11, 0, 63, 0])));
    return concatBytes(chunks);
  }

  private appendEntropyByte(value: number) {
    this.entropyChunk[this.entropyOffset++] = value;
    if (this.entropyOffset === this.entropyChunk.length) {
      this.chunks.push(this.entropyChunk.slice());
      this.entropyOffset = 0;
    }
  }

  private writeBits(value: number, length: number) {
    this.bitBuffer = (this.bitBuffer << length) | value;
    this.bitCount += length;
    while (this.bitCount >= 8) {
      const byte = (this.bitBuffer >> (this.bitCount - 8)) & 255;
      this.appendEntropyByte(byte);
      if (byte === 0xff) this.appendEntropyByte(0);
      this.bitCount -= 8;
      this.bitBuffer &= this.bitCount > 0 ? (1 << this.bitCount) - 1 : 0;
    }
  }

  private block(values: number[], component: number) {
    const q = this.qTables[component === 0 ? 0 : 1]!;
    const coefficients = new Array<number>(64).fill(0);
    for (let v = 0; v < 8; v += 1) for (let u = 0; u < 8; u += 1) {
      let sum = 0;
      for (let y = 0; y < 8; y += 1) for (let x = 0; x < 8; x += 1) sum += values[y * 8 + x]! * Math.cos(((2 * x + 1) * u * Math.PI) / 16) * Math.cos(((2 * y + 1) * v * Math.PI) / 16);
      const cu = u === 0 ? 1 / Math.sqrt(2) : 1; const cv = v === 0 ? 1 / Math.sqrt(2) : 1;
      coefficients[v * 8 + u] = Math.round((0.25 * cu * cv * sum) / q[v * 8 + u]!);
    }
    const dcValue = coefficients[0] as number;
    const diff = dcValue - this.dc[component]!; this.dc[component] = dcValue;
    const dcSize = magnitude(diff); const dcCode = this.dcTables[component === 0 ? 0 : 1]![dcSize] as HuffmanCode;
    this.writeBits(dcCode.code, dcCode.length); if (dcSize) this.writeBits(magnitudeBits(diff, dcSize), dcSize);
    let zeroRun = 0;
    for (let i = 1; i < 64; i += 1) {
      const value = coefficients[ZIGZAG[i] as number] as number;
      if (value === 0) { zeroRun += 1; continue; }
      while (zeroRun >= 16) { const zrl = this.acTables[component === 0 ? 0 : 1]![0xf0] as HuffmanCode; this.writeBits(zrl.code, zrl.length); zeroRun -= 16; }
      const size = magnitude(value); const code = this.acTables[component === 0 ? 0 : 1]![(zeroRun << 4) | size] as HuffmanCode; this.writeBits(code.code, code.length); this.writeBits(magnitudeBits(value, size), size); zeroRun = 0;
    }
    if (zeroRun) { const eob = this.acTables[component === 0 ? 0 : 1]![0] as HuffmanCode; this.writeBits(eob.code, eob.length); }
  }

  pushBand(rgb: Uint8Array, bandWidth: number, bandHeight: number, isLastBand: boolean) {
    const get = (x: number, y: number, component: number): number => {
      const clampedY = Math.min(bandHeight - 1, y);
      const index = (clampedY * bandWidth + Math.min(bandWidth - 1, x)) * 3;
      const r = rgb[index] as number; const g = rgb[index + 1] as number; const b = rgb[index + 2] as number;
      if (component === 0) return r - 128;
      if (component === 1) return g - 128;
      return b - 128;
    };
    for (let y = 0; y < bandHeight; y += 8) for (let x = 0; x < this.width; x += 8) {
      for (let component = 0; component < 3; component += 1) {
        const block = new Array<number>(64);
        for (let by = 0; by < 8; by += 1) for (let bx = 0; bx < 8; bx += 1) block[by * 8 + bx] = get(x + bx, y + by, component);
        this.block(block, component);
      }
    }
    if (isLastBand && this.bitCount > 0) { this.writeBits((1 << (8 - (this.bitCount % 8))) - 1, 8 - (this.bitCount % 8)); }
  }

  finish(): Uint8Array {
    if (this.entropyOffset > 0) this.chunks.push(this.entropyChunk.slice(0, this.entropyOffset));
    this.entropyOffset = 0;
    return concatBytes([...this.chunks, Uint8Array.from([0xff, 0xd9])]);
  }
}

export interface ParsedTiffRgb {
  width: number;
  height: number;
  bitsPerSample: number[];
  compression: number;
  photometric: number;
  samplesPerPixel: number;
  xResolution: number;
  yResolution: number;
  resolutionUnit: number;
  stripOffsets: number[];
  stripByteCounts: number[];
  icc: Uint8Array;
}

const readAsciiFourcc = (bytes: Uint8Array, offset: number): string => String.fromCharCode(bytes[offset] ?? 0, bytes[offset + 1] ?? 0, bytes[offset + 2] ?? 0, bytes[offset + 3] ?? 0);

export const parseTiffRgb = (bytes: Uint8Array): ParsedTiffRgb => {
  if (bytes[0] !== 0x49 || bytes[1] !== 0x49 || bytes[2] !== 0x2a || bytes[3] !== 0) throw new Error('Invalid TIFF header');
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const ifd = view.getUint32(4, true);
  const count = view.getUint16(ifd, true);
  const tags = new Map<number, { type: number; count: number; value: number }>();
  for (let index = 0; index < count; index += 1) {
    const offset = ifd + 2 + index * 12;
    tags.set(view.getUint16(offset, true), { type: view.getUint16(offset + 2, true), count: view.getUint32(offset + 4, true), value: view.getUint32(offset + 8, true) });
  }
  const value = (tag: number): number[] => {
    const entry = tags.get(tag);
    if (!entry) throw new Error(`Missing TIFF tag ${tag}`);
    const entryOffset = ifd + 2 + Array.from(tags.keys()).indexOf(tag) * 12;
    if (entry.type === 3) {
      const dataOffset = entry.count <= 2 ? entryOffset + 8 : entry.value;
      return Array.from({ length: entry.count }, (_, index) => view.getUint16(dataOffset + index * 2, true));
    }
    if (entry.type === 4) return Array.from({ length: entry.count }, (_, index) => view.getUint32(entry.count === 1 ? entryOffset + 8 : entry.value + index * 4, true));
    if (entry.type === 5) return [view.getUint32(entry.value, true) / view.getUint32(entry.value + 4, true)];
    return [entry.value];
  };
  const bits = value(258);
  const x = value(282)[0] as number;
  const y = value(283)[0] as number;
  const iccEntry = tags.get(34675) as { value: number; count: number };
  return { width: value(256)[0] as number, height: value(257)[0] as number, bitsPerSample: bits, compression: value(259)[0] as number, photometric: value(262)[0] as number, samplesPerPixel: value(277)[0] as number, xResolution: x, yResolution: y, resolutionUnit: value(296)[0] as number, stripOffsets: value(273), stripByteCounts: value(279), icc: bytes.slice(iccEntry.value, iccEntry.value + iccEntry.count) };
};

export interface ParsedJpegMetadata {
  quality100: boolean;
  sampling: number[];
  xDensity: number;
  yDensity: number;
  adobeTransform: number;
  icc: Uint8Array;
}

export const parseJpegMetadata = (bytes: Uint8Array): ParsedJpegMetadata => {
  if (bytes[0] !== 0xff || bytes[1] !== 0xd8) throw new Error('Invalid JPEG header');
  let offset = 2; let xDensity = 0; let yDensity = 0; let adobeTransform = -1; let quality100 = false; let sampling: number[] = []; const profiles: Uint8Array[] = [];
  while (offset + 3 < bytes.length) {
    if (bytes[offset] !== 0xff) { offset += 1; continue; }
    const marker = bytes[offset + 1]; if (marker === 0xd9) break;
    const length = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength).getUint16(offset + 2, false);
    const payloadStart = offset + 4; const payloadEnd = offset + 2 + length; const payload = bytes.slice(payloadStart, payloadEnd);
    if (marker === 0xe0 && String.fromCharCode(...payload.slice(0, 5)) === 'JFIF\0') { xDensity = (payload[8] as number) * 256 + (payload[9] as number); yDensity = (payload[10] as number) * 256 + (payload[11] as number); }
    if (marker === 0xee && String.fromCharCode(...payload.slice(0, 5)) === 'Adobe') adobeTransform = payload[11] as number;
    if (marker === 0xe2 && String.fromCharCode(...payload.slice(0, 12)) === 'ICC_PROFILE\0') profiles.push(payload.slice(14));
    if (marker === 0xdb) quality100 = payload.every((value, index) => index % 65 === 0 || value === 1);
    if (marker === 0xc0) for (let index = 0; index < (payload[5] as number); index += 1) sampling.push(payload[7 + index * 3] as number);
    offset = payloadEnd;
  }
  const icc = new Uint8Array(profiles.reduce((sum, part) => sum + part.length, 0)); let profileOffset = 0; profiles.forEach((part) => { icc.set(part, profileOffset); profileOffset += part.length; });
  return { quality100, sampling, xDensity, yDensity, adobeTransform, icc };
};

export const PRINT_ICC_MANIFEST: Readonly<Record<PrintIccProfile, { path: string; sha256: string }>> = {
  'adobe-rgb-1998': {
    path: '/assets/icc/adobe-rgb-1998.icc',
    sha256: 'ffe70d8ae23c5b02735bfcda29945ce09cb520d0dd48ac7722b76b614e723350',
  },
  srgb: {
    path: '/assets/icc/srgb-iec61966-2.1.icc',
    sha256: '41e0be558e234a02204ec02fd82760405047d0198ec4ce57d95cacd64151170c',
  },
  'iso-coated-v2-300-eci': {
    path: '/assets/icc/iso-coated-v2-300-eci.icc',
    sha256: 'c6b4b62f0726243742eced8b9669476a6be89e581f50a7600ed8b6fcbb9cdab8',
  },
};

export const readPrintIccDescription = (bytes: Uint8Array): string | null => {
  if (bytes.byteLength < 132) return null;
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const tagCount = view.getUint32(128, false);
  if (132 + tagCount * 12 > bytes.byteLength) return null;
  for (let index = 0; index < tagCount; index += 1) {
    const offset = 132 + index * 12;
    if (view.getUint32(offset, false) !== 0x64657363) continue;
    const dataOffset = view.getUint32(offset + 4, false);
    const dataSize = view.getUint32(offset + 8, false);
    if (dataOffset + 12 > bytes.byteLength || dataOffset + dataSize > bytes.byteLength || view.getUint32(dataOffset, false) !== 0x64657363) return null;
    const asciiLength = view.getUint32(dataOffset + 8, false);
    if (asciiLength < 1 || dataOffset + 12 + asciiLength > bytes.byteLength) return null;
    return String.fromCharCode(...bytes.slice(dataOffset + 12, dataOffset + 12 + asciiLength - 1));
  }
  return null;
};

export const validatePrintIccBytes = async (profile: PrintIccProfile, bytes: Uint8Array): Promise<boolean> => {
  const metadata = PRINT_ICC_PROFILES[profile];
  if (!metadata || bytes.byteLength < 132 || bytes[36] !== 0x61 || bytes[37] !== 0x63 || bytes[38] !== 0x73 || bytes[39] !== 0x70) return false;
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  if (view.getUint32(0, false) !== bytes.byteLength || String.fromCharCode(...bytes.slice(16, 20)) !== (metadata.colorSpace === 'cmyk' ? 'CMYK' : 'RGB ')) return false;
  const tagCount = view.getUint32(128, false);
  if (tagCount > 1024 || 132 + tagCount * 12 > bytes.byteLength) return false;
  const requiredTags = metadata.colorSpace === 'cmyk'
    ? new Map([[0x64657363, 0x64657363], [0x77747074, 0x58595a20], [0x41324230, 0x6d667432], [0x42324130, 0x6d667432], [0x6b545243, 0x63757276]])
    : new Map([[0x64657363, 0x64657363], [0x77747074, 0x58595a20], [0x7258595a, 0x58595a20], [0x6758595a, 0x58595a20], [0x6258595a, 0x58595a20], [0x72545243, 0x63757276], [0x67545243, 0x63757276], [0x62545243, 0x63757276]]);
  for (let index = 0; index < tagCount; index += 1) {
    const offset = 132 + index * 12;
    const tag = view.getUint32(offset, false);
    const dataOffset = view.getUint32(offset + 4, false);
    const dataSize = view.getUint32(offset + 8, false);
    const expected = requiredTags.get(tag);
    if (expected !== undefined && dataOffset >= 132 && dataSize >= 4 && dataOffset + dataSize <= bytes.byteLength && view.getUint32(dataOffset, false) === expected) requiredTags.delete(tag);
  }
  if (requiredTags.size > 0 || readPrintIccDescription(bytes) !== metadata.description || !globalThis.crypto?.subtle) return false;
  const digest = await globalThis.crypto.subtle.digest('SHA-256', bytes.slice().buffer);
  const actual = Array.from(new Uint8Array(digest), (value) => value.toString(16).padStart(2, '0')).join('');
  return actual === PRINT_ICC_MANIFEST[profile].sha256;
};

export type ExportPickerExtension = ImageExportFileFormat | 'mp4' | 'mov' | 'jpg' | 'tif';

type NativeSavePickerWritable = {
  write: (data: Blob | BufferSource | string) => Promise<void>;
  close: () => Promise<void>;
  abort?: () => Promise<void>;
};

type NativeSavePickerFileHandle = {
  name?: string;
  createWritable: () => Promise<NativeSavePickerWritable>;
};

type NativeSavePickerOptions = {
  suggestedName?: string;
  types?: Array<{ description: string; accept: Record<string, string[]> }>;
  excludeAcceptAllOption?: boolean;
};

type NativeSavePickerWindow = Window & {
  showSaveFilePicker?: (options?: NativeSavePickerOptions) => Promise<NativeSavePickerFileHandle>;
};

export type ExportSaveTarget =
  | { kind: 'native'; handle: NativeSavePickerFileHandle }
  | { kind: 'download' };

export const getExportPickerType = (extension: ExportPickerExtension): NativeSavePickerOptions['types'] => {
  if (extension === 'png') return [{ description: 'PNG Image', accept: { 'image/png': ['.png'] } }];
  if (extension === 'webp') return [{ description: 'WebP Image', accept: { 'image/webp': ['.webp'] } }];
  if (extension === 'jpeg' || extension === 'jpg') return [{ description: 'JPEG Image', accept: { 'image/jpeg': ['.jpg', '.jpeg'] } }];
  if (extension === 'svg') return [{ description: 'SVG Image', accept: { 'image/svg+xml': ['.svg'] } }];
  if (extension === 'pdf') return [{ description: 'PDF Document', accept: { 'application/pdf': ['.pdf'] } }];
  if (extension === 'tif') return [{ description: 'TIFF Image', accept: { 'image/tiff': ['.tif', '.tiff'] } }];
  if (extension === 'mov') return [{ description: 'QuickTime Video', accept: { 'video/quicktime': ['.mov'] } }];
  return [{ description: 'MP4 Video', accept: { 'video/mp4': ['.mp4'] } }];
};

export const isNativePickerAbort = (error: unknown): boolean => error instanceof DOMException && error.name === 'AbortError';

export const requestExportSaveTarget = async (suggestedName: string, extension: ExportPickerExtension): Promise<ExportSaveTarget | null> => {
  if (typeof window === 'undefined') return { kind: 'download' };
  const picker = (window as NativeSavePickerWindow).showSaveFilePicker;
  if (typeof picker !== 'function') return { kind: 'download' };
  try {
    const handle = await picker.call(window, { suggestedName, types: getExportPickerType(extension), excludeAcceptAllOption: false });
    return { kind: 'native', handle };
  } catch (error) {
    if (isNativePickerAbort(error)) return null;
    console.warn('[ImageExportModal] Native save picker unavailable; falling back to browser download.', error);
    return { kind: 'download' };
  }
};

export const saveExportBlob = async (blob: Blob, fileName: string, target: ExportSaveTarget): Promise<string> => {
  if (target.kind === 'native') {
    const writable = await target.handle.createWritable();
    try {
      await writable.write(blob);
      await writable.close();
    } catch (error) {
      await writable.abort?.().catch(() => undefined);
      throw error;
    }
    return target.handle.name || fileName;
  }
  const objectUrl = URL.createObjectURL(blob);
  try {
    const link = document.createElement('a'); link.href = objectUrl; link.download = fileName;
    document.body.appendChild(link); link.click(); document.body.removeChild(link);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
  return fileName;
};

export interface PrintTilePixels {
  x: number;
  width: number;
  rgb: Uint8Array;
}

export const assemblePrintTileBand = ({
  width,
  bandHeight,
  tiles,
}: {
  width: number;
  bandHeight: number;
  tiles: PrintTilePixels[];
}): Uint8Array => {
  const rgb = new Uint8Array(width * bandHeight * 3);
  for (const tile of tiles) {
    for (let row = 0; row < bandHeight; row += 1) {
      rgb.set(tile.rgb.subarray(row * tile.width * 3, (row + 1) * tile.width * 3), (row * width + tile.x) * 3);
    }
  }
  return rgb;
};

export const printBandProgress = (bandY: number, bandHeight: number, totalHeight: number): number => (
  Math.min(99, Math.round(((bandY + bandHeight) / Math.max(1, totalHeight)) * 100))
);

export const PRINT_SOURCE_LOAD_FAILED = 'PRINT_SOURCE_LOAD_FAILED';
export const PRINT_UNSUPPORTED_LAYER = 'PRINT_UNSUPPORTED_LAYER';

const sourceFromLayer = (layer: Layer): PrintSnapshotResource | null => {
  if (layer.imageData) {
    const copy = new Uint8Array(layer.imageData.data).slice();
    return { kind: 'arrayBuffer', value: copy.buffer, encoding: 'rgba8', width: layer.imageData.width, height: layer.imageData.height };
  }
  const svgSource = layer.type === 'svg' || layer.params
    ? getDrawingAwareSvgLayerSource(layer)
    : null;
  const source = svgSource || (typeof layer.imageSrc === 'string' ? layer.imageSrc.trim() : '');
  if (!source) return null;
  if (typeof window !== 'undefined' && /^(https?:)?\/\//i.test(source)) {
    try {
      if (new URL(source, window.location.href).origin !== window.location.origin) return null;
    } catch {
      return null;
    }
  }
  return { kind: 'url', value: source };
};

export const buildPrintSnapshot = ({
  layers,
  areaRect,
  frameRect,
  width,
  height,
  options,
  icc,
}: {
  layers: Layer[];
  areaRect: Rect;
  frameRect: Rect;
  width: number;
  height: number;
  options: PrintExportOptions;
  icc: ArrayBuffer;
}): PrintSnapshot => {
  const error = validatePrintExportOptions(options);
  if (error) throw new Error(error);
  const dimensions = resolvePrintDimensions(options);
  if (dimensions.width !== width || dimensions.height !== height) throw new Error('PRINT_INVALID_DIMENSIONS');
  const bleedPx = Math.round((options.bleedMm / 25.4) * options.dpi);
  const contentWidth = Math.max(1, width - bleedPx * 2);
  const contentHeight = Math.max(1, height - bleedPx * 2);
  const snapshotLayers: PrintSnapshotLayer[] = [];
  for (const layer of layers) {
    if (layer.visible === false) continue;
    if (layer.type !== undefined && layer.type !== 'svg') throw new Error(`${PRINT_UNSUPPORTED_LAYER}:${layer.id}`);
    const source = sourceFromLayer(layer);
    if (!source) throw new Error(`${PRINT_SOURCE_LOAD_FAILED}:${layer.id}`);
    const sourceWidth = layer.imageData?.width ?? 1;
    const sourceHeight = layer.imageData?.height ?? 1;
    const layerWidth = Number.isFinite(layer.rect?.w) && layer.rect.w > 0
      ? layer.rect.w
      : sourceWidth * (typeof layer.scale === 'number' && layer.scale > 0 ? layer.scale : 1);
    const layerHeight = Number.isFinite(layer.rect?.h) && layer.rect.h > 0
      ? layer.rect.h
      : sourceHeight * (typeof layer.scale === 'number' && layer.scale > 0 ? layer.scale : 1);
    const worldX = frameRect.x + (layer.rect?.x ?? 0);
    const worldY = frameRect.y + (layer.rect?.y ?? 0);
    const x = ((worldX - areaRect.x) / Math.max(1, areaRect.w)) * contentWidth;
    const y = ((worldY - areaRect.y) / Math.max(1, areaRect.h)) * contentHeight;
    const widthPx = (layerWidth / Math.max(1, areaRect.w)) * contentWidth;
    const heightPx = (layerHeight / Math.max(1, areaRect.h)) * contentHeight;
    const transform = resolveStudioLayerTransform(layer.params);
    snapshotLayers.push({
      id: layer.id,
      source,
      x,
      y,
      width: widthPx,
      height: heightPx,
      opacity: typeof layer.opacity === 'number' && Number.isFinite(layer.opacity) ? layer.opacity : 1,
      rotationDeg: transform.rotationDeg,
      flipX: transform.flipX,
      flipY: transform.flipY,
      renderOrder: (typeof layer.renderOrder === 'number' ? layer.renderOrder : snapshotLayers.length) + (layer['top-layer'] === true ? 1_000_000 : 0),
    });
  }
  snapshotLayers.sort((a, b) => a.renderOrder - b.renderOrder || a.id.localeCompare(b.id));
  return { version: 1, width, height, dpi: options.dpi, format: options.format, bleedPx, icc, iccProfile: options.iccProfile, layers: snapshotLayers };
};

const loadProfile = async (profile: PrintExportOptions['iccProfile']): Promise<ArrayBuffer> => {
  const path = PRINT_ICC_PROFILES[profile]?.path;
  if (!path) throw new Error('PRINT_INVALID_ICC');
  let response: Response;
  try {
    response = await fetch(path, { credentials: 'same-origin' });
  } catch {
    throw new Error('PRINT_ICC_PROFILE_MISSING');
  }
  if (!response.ok) throw new Error('PRINT_ICC_PROFILE_MISSING');
  const bytes = new Uint8Array(await response.arrayBuffer());
  if (!(await validatePrintIccBytes(profile, bytes))) throw new Error('PRINT_ICC_PROFILE_INVALID');
  return bytes.buffer;
};

export const loadPrintIccProfile = loadProfile;
